import React from 'react';
import {
  FormControl,
  FormLabel,
  FormHelperText,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  styled,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import { useSelect } from './useSelect';

export const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.grey[800],
  '&.Mui-focused': {
    color: theme.palette.grey[800],
  },
}));

const StyledFormControl = styled(FormControl)({
  width: '100%',
});

const StyledSelect = styled(MuiSelect)({
  width: '100%',
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  flexDirection: 'row',
  minHeight: 'auto',
  gap: theme.spacing(1),
}));

type MuiSelectOmmitKeys = 'children' | 'options' | 'label' | 'id' | 'value';

export type OmittedMuiSelectProps = Omit<MuiSelectProps, MuiSelectOmmitKeys>;

export interface SelectProps extends OmittedMuiSelectProps {
  // Explicitly calling out id as required
  id: string;
  label?: string;
  value: string;
  options: Record<string, any>[];
  optionLabelKey?: 'label' | string;
  optionValueKey?: 'value' | string;
  helperText?: string;
  labelId?: string;
}

export const Select = ({
  id,
  label,
  value,
  optionLabelKey = 'label',
  optionValueKey = 'value',
  options: ontionData,
  helperText,
  variant = 'outlined',
  labelId,
  ...restProps
}: SelectProps) => {
  const { options } = useSelect(ontionData, optionLabelKey, optionValueKey, label);
  const errorId = `${id}-helper-text`;
  const hasError = !!restProps.error;

  const resolvedLabelId = labelId || `${id}-label`;

  return (
    <StyledFormControl error={hasError}>
      {label && <StyledFormLabel id={resolvedLabelId}>{label}</StyledFormLabel>}
      <StyledSelect
        id={id}
        value={value}
        variant={variant}
        renderValue={(selected: unknown) => <Typography>{selected as string}</Typography>}
        labelId={resolvedLabelId}
        aria-describedby={helperText ? errorId : undefined}
        aria-invalid={hasError}
        MenuProps={{
          disableEnforceFocus: true,
          MenuListProps: {
            sx: {
              maxHeight: '30vh',
            },
          },
        }}
        {...restProps}
      >
        {options.map((option) => (
          <StyledMenuItem
            key={option.value}
            value={option.value}
            aria-selected={value === option.value}
          >
            <DoneIcon
              style={{
                visibility: value === option.value ? 'visible' : 'hidden',
              }}
              fontSize="small"
              aria-hidden="true"
            />
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      <FormHelperText id={errorId} error={hasError}>
        {hasError && helperText ? helperText : ''}
      </FormHelperText>
    </StyledFormControl>
  );
};
